"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useCartDrawer } from "src/contexts";
import {
  CHAT_WIDGET_PLACEMENT_CSS,
  chatLayout,
  chatTuckMode,
  measureChatBars,
  type Rect,
} from "src/utils/chat-widget-placement";

/**
 * Keeps the third-party chat launcher above the sticky bottom CTAs (release
 * walk B7, verify r4); see `chat-widget-placement.ts`. Everything goes on
 * <html> from effects, so the server and the first client render match.
 *
 * The bars come and go (the drawer opens, the upload bar appears, a step
 * changes), so the layout is re-measured on resize, on scroll and on a short
 * interval; each pass is one attribute query over a handful of elements.
 */
export function ChatWidgetPlacement() {
  const pathname = usePathname();
  const { isCartDrawerOpen } = useCartDrawer();
  const mode = chatTuckMode(pathname, isCartDrawerOpen);

  useEffect(() => {
    const root = document.documentElement;
    if (mode) root.dataset.chatTuck = mode;
    else delete root.dataset.chatTuck;
    return () => {
      delete root.dataset.chatTuck;
    };
  }, [mode]);

  useEffect(() => {
    const root = document.documentElement;
    let frame = 0;
    const apply = () => {
      frame = 0;
      const el = document.getElementById("chat-widget-bubble");
      // The launcher's column; its height is ignored, only x matters.
      let launcher: Rect | null = null;
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0) launcher = { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
      }
      const layout = chatLayout({
        viewportHeight: window.innerHeight,
        bars: measureChatBars(document),
        launcher,
      });
      if (root.dataset.chatLayout !== layout.kind) root.dataset.chatLayout = layout.kind;
      const lift = layout.kind === "lift" ? `${layout.liftPx}px` : "";
      if (root.style.getPropertyValue("--chat-lift") !== lift) {
        if (lift) root.style.setProperty("--chat-lift", lift);
        else root.style.removeProperty("--chat-lift");
      }
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(apply);
    };
    schedule();
    const timer = window.setInterval(schedule, 400);
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { passive: true });
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule);
      if (frame) window.cancelAnimationFrame(frame);
      delete root.dataset.chatLayout;
      root.style.removeProperty("--chat-lift");
    };
  }, [pathname, isCartDrawerOpen]);

  // Raw, not a text child: React escapes the quotes in the attribute selectors.
  return <style dangerouslySetInnerHTML={{ __html: CHAT_WIDGET_PLACEMENT_CSS }} />;
}
